import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EmailEditor from 'react-email-editor';
import { ChevronLeft, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { CUSTOM_BLOCK_MERGE_TAGS } from '../lib/emailBlocks';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MERGE_TAGS = {
  first_name:      { name: 'First Name',      value: '{{first_name}}',      sample: 'John' },
  last_name:       { name: 'Last Name',       value: '{{last_name}}',       sample: 'Doe' },
  email:           { name: 'Email',           value: '{{email}}',           sample: 'john@example.com' },
  order_number:    { name: 'Order Number',    value: '{{order_number}}',    sample: '#1234' },
  order_total:     { name: 'Order Total',     value: '{{order_total}}',     sample: '$99.00' },
  company_name:    { name: 'Company Name',    value: '{{company_name}}',    sample: 'Your Brand' },
  unsubscribe_url: { name: 'Unsubscribe URL', value: '{{unsubscribe_url}}', sample: '#' },
  // Merge tags exposed by the custom blocks (abandoned cart, store
  // credits, order details, driver details, courier tracking).
  ...CUSTOM_BLOCK_MERGE_TAGS,
};

const CATEGORIES = [
  { id: 'marketing', label: 'Marketing' },
  { id: 'transactional', label: 'Transactional' },
  { id: 'newsletter', label: 'Newsletter' },
  { id: 'custom', label: 'Custom' },
];

const EmailTemplateEditorPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [editorReady, setEditorReady] = useState(false);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [testAccountId, setTestAccountId] = useState('');

  // Real-data preview: pick a recent order so the Order Details /
  // Timeline / Driver / Courier blocks render with actual values
  // instead of the `Test User` / `#1234` placeholders. When empty,
  // the legacy sample-data path is used (no behaviour change).
  const [recentOrders, setRecentOrders] = useState([]);
  const [testOrderId, setTestOrderId] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, accRes] = await Promise.all([
        axios.get(`${API}/email-templates/${templateId}`, { headers }),
        axios.get(`${API}/email/accounts`, { headers }).catch(() => ({ data: { accounts: [] } })),
      ]);
      setTemplate(tRes.data);
      const accs = accRes.data.accounts || accRes.data || [];
      setEmailAccounts(Array.isArray(accs) ? accs : []);
      if (accs[0]?.id) setTestAccountId(accs[0].id);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load template');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  // Lazy-load recent orders when the Send Test dialog opens. Cheap
  // (capped at 20 rows server-side) — only fired once per dialog open
  // to keep the picker snappy on tenants with thousands of orders.
  useEffect(() => {
    if (!testOpen || recentOrders.length > 0) return;
    let cancelled = false;
    axios.get(`${API}/email-templates/preview-data/recent-orders?limit=20`, { headers })
      .then((res) => {
        if (cancelled) return;
        setRecentOrders(res.data?.orders || []);
      })
      .catch(() => { /* non-fatal — falls back to sample data */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testOpen]);

  const onEditorLoad = (unlayer) => {
    editorRef.current = unlayer;
    // NOTE: react-email-editor v1.8 — `unlayer.registerTool` on the
    // PARENT wrapper is a silent no-op. Custom tools are instead
    // registered from INSIDE the iframe via `options.customJS` which
    // points at /email-blocks.js (served from /public). Merge tags
    // CAN be configured from the parent, so we still do that here.
    setEditorReady(true);
    unlayer.setMergeTags(MERGE_TAGS);
    if (template?.design_json) {
      try { unlayer.loadDesign(template.design_json); }
      catch (e) { console.warn('Failed to load design', e); }
    }
  };

  // Once both editor and template are ready, load design
  useEffect(() => {
    if (editorReady && template?.design_json && editorRef.current) {
      try { editorRef.current.loadDesign(template.design_json); }
      catch (e) { console.warn('Design load:', e); }
    }
  }, [editorReady, template]);

  const save = async () => {
    if (!editorRef.current) { toast.error('Editor not ready'); return; }
    setSaving(true);
    editorRef.current.exportHtml((data) => {
      (async () => {
        try {
          const { design, html } = data;
          // Generate body_text by stripping HTML (simple fallback)
          const bodyText = (html || '').replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
          await axios.put(`${API}/email-templates/${templateId}`, {
            body_html: html,
            body_text: bodyText,
            design_json: design,
            subject: template.subject,
            name: template.name,
            category: template.category,
            preview_text: template.preview_text,
          }, { headers });
          toast.success('Template saved');
        } catch (e) {
          toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
        } finally { setSaving(false); }
      })();
    });
  };

  const sendTest = async () => {
    if (!testEmail || !testAccountId) { toast.error('Enter test email and pick a from account'); return; }
    // First save the latest content
    if (!editorRef.current) return;
    editorRef.current.exportHtml((data) => {
      (async () => {
        try {
          const { design, html } = data;
          await axios.put(`${API}/email-templates/${templateId}`, {
            body_html: html,
            design_json: design,
            subject: template.subject,
            name: template.name,
          }, { headers });
          await axios.post(`${API}/email-templates/${templateId}/send-test`, {
            to_email: testEmail,
            from_account_id: testAccountId,
            // Optional: when set, the backend uses the merge-tag
            // resolver against THIS order (Order Details / Timeline /
            // Courier / Driver blocks render with real data).
            order_id: testOrderId || undefined,
          }, { headers });
          toast.success(
            testOrderId
              ? `Test email (using order ${recentOrders.find(o => o.id === testOrderId)?.order_number || testOrderId.slice(0, 8)}) sent to ${testEmail}`
              : `Test email sent to ${testEmail}`,
          );
          setTestOpen(false);
        } catch (e) {
          toast.error('Send test failed: ' + (e?.response?.data?.detail || e.message));
        }
      })();
    });
  };

  if (loading || !template) {
    return <div className="p-8 text-gray-400">Loading template…</div>;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="email-template-editor">
      <div className="flex items-center justify-between gap-3 p-3 border-b border-wt-border bg-wt-surface flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[300px]">
          <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/templates')} data-testid="back-to-templates">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Input
            value={template.name}
            onChange={(e) => setTemplate({ ...template, name: e.target.value })}
            className="bg-wt-bg border-wt-border font-semibold max-w-xs"
            placeholder="Template name"
            data-testid="tpl-name-input"
          />
          <Input
            value={template.subject}
            onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
            className="bg-wt-bg border-wt-border flex-1 min-w-[200px]"
            placeholder="Email subject (supports {{variables}})"
            data-testid="tpl-subject-input"
          />
          <Select value={template.category} onValueChange={(v) => setTemplate({ ...template, category: v })}>
            <SelectTrigger className="w-36 bg-wt-bg"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setTestOpen(true)} data-testid="send-test-btn">
            <Send className="w-4 h-4 mr-1" /> Send Test
          </Button>
          <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-amber-500 to-pink-500 hover:opacity-90" data-testid="save-tpl-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Template'}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden bg-white">
        <EmailEditor
          ref={editorRef}
          onLoad={onEditorLoad}
          minHeight="100%"
          options={{
            mergeTags: MERGE_TAGS,
            displayMode: 'email',
            // Unlock the advanced editor surface area: image gallery,
            // stock photos, video, timer, social, sticker library,
            // AI-assist textGen, etc. (everything Unlayer exposes for
            // free without an enterprise account).
            features: {
              stockImages: true,
              preview: true,
              imageEditor: true,
              undoRedo: true,
              textEditor: {
                spellChecker: true,
                tables: true,
                emojis: true,
                cleanPaste: true,
                inlineFontControls: true,
              },
              audit: false,
              ai: { enabled: true },
              smartMergeTags: true,
              userUploads: true,
              sendTestEmail: false,   // we have our own Send Test button
            },
            tools: {
              // Make sure the built-in transactional-friendly tools
              // (timer, video, html, menu, social, dynamic) are all
              // visible. `enabled: true` is the explicit default but
              // listing them keeps the config self-documenting.
              timer:    { enabled: true },
              video:    { enabled: true },
              html:     { enabled: true },
              menu:     { enabled: true },
              social:   { enabled: true },
              divider:  { enabled: true },
              heading:  { enabled: true },
              text:     { enabled: true },
              image:    { enabled: true },
              button:   { enabled: true },
              columns:  { enabled: true },
              form:     { enabled: true },
            },
            appearance: {
              theme: 'modern_light',
              panels: {
                tools: { dock: 'left' },
              },
              loader: {
                html: '<div style="padding:24px;font:14px sans-serif;color:#6b7280">Loading editor…</div>',
              },
            },
            // Custom blocks live in /public/email-blocks.js and are
            // registered from inside the Unlayer iframe via this
            // customJS hook. Absolute URL is required — the iframe
            // can't resolve relative paths to the parent's origin.
            customJS: [
              (typeof window !== 'undefined' ? window.location.origin : '') + '/email-blocks.js',
            ],
          }}
        />
      </div>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="bg-wt-surface border-wt-border">
          <DialogHeader><DialogTitle className="text-white">Send Test Email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white">Send to</Label>
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" className="bg-wt-bg mt-1" type="email" data-testid="test-to-input" />
            </div>
            <div>
              <Label className="text-white">From Account</Label>
              {emailAccounts.length === 0 ? (
                <div className="text-xs text-red-400 mt-1">No email accounts configured. Go to Integrations → Email Integration first.</div>
              ) : (
                <Select value={testAccountId} onValueChange={setTestAccountId}>
                  <SelectTrigger className="bg-wt-bg mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {emailAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.email || a.account_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            {/* Real-data preview picker. Optional — when empty, the
                test send falls back to placeholder sample values. */}
            <div>
              <Label className="text-white flex items-center gap-2">
                Use real order data
                <span className="text-[10px] uppercase tracking-wide text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">Optional</span>
              </Label>
              <Select value={testOrderId || 'none'} onValueChange={(v) => setTestOrderId(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-wt-bg mt-1" data-testid="test-order-picker">
                  <SelectValue placeholder="Use placeholder sample values" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="none">— Use placeholder sample values —</SelectItem>
                  {recentOrders.map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`test-order-option-${o.id}`}>
                      {(o.order_number || o.id.slice(0, 8))}
                      {o.customer_name ? ` • ${o.customer_name}` : ''}
                      {o.status ? ` • ${o.status}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 mt-1">
                Pick a recent order to render Order Details, Timeline, Driver & Courier blocks with real data.
              </p>
            </div>
            <div className="text-xs text-gray-500">
              {testOrderId
                ? <>Merge tags will be filled from <strong className="text-amber-300">order {recentOrders.find(o => o.id === testOrderId)?.order_number || testOrderId.slice(0, 8)}</strong>.</>
                : <>Merge tags will be filled with sample values (e.g., {'{{first_name}}'} → "Test").</>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={sendTest} disabled={!testEmail || !testAccountId} data-testid="submit-test-btn">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplateEditorPage;
